/**
 * WorkflowSignOff - Step 3 (final) of the work order completion workflow.
 *
 * The primary technician provides a digital signature and optional notes
 * to finalize the work order. Once submitted, the work order status
 * transitions to "completed".
 *
 * Features:
 * - Digital signature capture via SignatureCapture organism
 * - Optional notes field for additional comments
 * - Clear submission confirmation
 * - Success state showing the completed submission
 *
 * The notes box is seeded with whatever the crew already wrote in the Job
 * Details panel (`initialNotes`) — both write work_orders.checklist_notes, so
 * they are one field seen twice, not two competing ones. Editing here edits
 * those same notes; clearing the box clears them.
 *
 * It also asks "how long did this take?", per person (`crew`), on every work
 * order: the actual-hours half of estimate-versus-actual. Seeded with the
 * hours already on the record (`initialHours`), handed back to the parent on
 * submit, and — on an hour-tracked job (`hoursRequired`) — holding the
 * Submit button until every person carries a number.
 */
import React, { useEffect, useState } from 'react';
import { Alert } from '../../atoms';
import type { WorkOrderChecklist } from '../../../services/checklistService';
import SignatureCapture from '../../organisms/SignatureCapture/SignatureCapture';
import Button from '../../atoms/Button/Button';
import { formatSignOffTimestamp } from '../../../utils/formatDate';
import { HoursWorkedFieldset, peopleMissingHours } from '../../molecules/HoursWorkedFieldset';
import type { HoursWorkedPerson } from '../../molecules/HoursWorkedFieldset';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface WorkflowSignOffProps {
    /** The checklist state (used to show submitted status) */
    checklist: WorkOrderChecklist;
    /** Handler for final sign-off submission */
    onSubmitSignOff: (signatureDataUrl: string, notes?: string, hours?: Record<string, string>) => Promise<void>;
    /** Handler to go back to the verify step */
    onBack: () => void;
    /**
     * Job notes already recorded on the work order (checklist_notes), used to
     * seed the textarea so a tech who wrote notes mid-job doesn't have to
     * retype them — or wipe them by signing off on an empty box.
     */
    initialNotes?: string | null;
    /**
     * The crew, primary first, for the "hours worked" prompt. Omit it (or pass
     * an empty list) and the prompt is hidden — the caller has no crew data.
     */
    crew?: HoursWorkedPerson[];
    /** Hours already on the record, keyed by user id. */
    initialHours?: Record<string, string>;
    /** True on an hour-tracked job: Submit is held until every person has hours. */
    hoursRequired?: boolean;
    /** Optional "Quick Add User" control for the hours prompt — the parent's
     *  QuickAddUserLink, wired to add the people it creates to `crew`. */
    quickAdd?: React.ReactNode;
    /** The job's hour bucket — the estimate — shown against the total when set. */
    estimatedHours?: number | null;
    /** Whether submission is in progress */
    isSubmitting: boolean;
    /** Error message to display */
    error: string | null;
    /** Whether the device is offline */
    isOffline: boolean;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const WorkflowSignOff: React.FC<WorkflowSignOffProps> = ({
    checklist,
    onSubmitSignOff,
    onBack,
    initialNotes,
    crew = [],
    initialHours,
    hoursRequired = false,
    estimatedHours = null,
    quickAdd,
    isSubmitting,
    error,
    isOffline,
    className,
}) => {
    // Local state for the signature and notes
    const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
    const [notes, setNotes] = useState(initialNotes ?? '');
    /* Hours worked, per person. Seeded from the record and re-seeded when it
       refetches, the same way the notes are. */
    const [hours, setHours] = useState<Record<string, string>>(initialHours ?? {});
    useEffect(() => {
        setHours(initialHours ?? {});
    }, [initialHours]);
    const missingHours = hoursRequired ? peopleMissingHours(crew, hours) : [];

    /* Re-seed when the stored notes change under us — the Job Details panel
       can save while this step is mounted (the tech steps back, writes a
       note, steps forward), and the box should show what was saved. */
    useEffect(() => {
        setNotes(initialNotes ?? '');
    }, [initialNotes]);

    /**
     * handleSubmit() - Validates the signature is captured and submits.
     */
    const handleSubmit = async () => {
        if (!signatureDataUrl || missingHours.length > 0) return;
        /* Always send the notes — including an empty string, which is the
           tech deliberately clearing them. The server only writes the column
           when the key is present, so omitting it would mean "leave them
           alone" and a clear would silently not take. Hours go along only
           when there was a crew to ask about. */
        await onSubmitSignOff(signatureDataUrl, notes, crew.length > 0 ? hours : undefined);
    };

    // --- Already submitted: show success state ---
    if (checklist.isSubmitted) {
        return (
            <div className={['workflow-sign-off', 'workflow-sign-off--submitted', 'flex flex-col h-full items-center justify-center px-24px', className ?? ''].join(' ')} data-ref="organism-workflow-sign-off">
                {/* Success checkmark circle */}
                <div className="w-80px h-80px rounded-full bg-green-100 flex items-center justify-center mb-20px">
                    <svg className="w-44px h-44px text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                </div>

                <h3 className="text-22px font-sans font-bold text-brand-text mb-8px text-center">
                    Work Order Submitted
                </h3>
                <p className="text-15px font-sans text-brand-text-muted text-center mb-4px">
                    Signed off by {checklist.submittedByName}
                </p>
                {checklist.submittedAt && (
                    <p className="text-13px font-sans text-brand-text-subtle text-center">
                        {formatSignOffTimestamp(checklist.submittedAt)}
                    </p>
                )}

                {/* Show the captured signature */}
                {checklist.signatureDataUrl && (
                    <div className="mt-24px w-full max-w-xs border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                        <img
                            src={checklist.signatureDataUrl}
                            alt="Submitted signature"
                            className="w-full h-auto"
                        />
                    </div>
                )}

                {/* Offline sync note */}
                {isOffline && (
                    <div className="flex items-center gap-6px mt-20px px-12px py-8px bg-amber-50 border border-amber-200 rounded-md">
                        <svg className="h-14px w-14px text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                        </svg>
                        <span className="text-12px font-sans text-amber-700">
                            This submission will sync when you're back online
                        </span>
                    </div>
                )}
            </div>
        );
    }

    // --- Sign-off form ---
    return (
        <div className={['workflow-sign-off', 'flex flex-col h-full', className ?? ''].join(' ')} data-ref="organism-workflow-sign-off">
            {/* --- Scrollable content area --- */}
            <div className="workflow-sign-off__body flex-1 overflow-y-auto px-16px pt-16px pb-16px">
                {/* Sign-off header */}
                <div className="mb-20px">
                    <div className="flex items-center gap-10px mb-8px">
                        {/* Pen icon */}
                        <div className="w-40px h-40px rounded-full bg-brand-primary/10 flex items-center justify-center">
                            <svg className="w-22px h-22px text-brand-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-18px font-sans font-bold text-brand-text">
                                Sign Off
                            </h3>
                            <p className="text-13px font-sans text-brand-text-muted">
                                Provide your signature to complete this work order
                            </p>
                        </div>
                    </div>
                </div>

                {/* Offline indicator */}
                {isOffline && (
                    <div className="flex items-center gap-6px mb-12px px-10px py-6px bg-amber-50 border border-amber-200 rounded-md">
                        <svg className="h-14px w-14px text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                        </svg>
                        <span className="text-12px font-sans text-amber-700">
                            Offline — sign-off will sync when you reconnect
                        </span>
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <Alert variant="error" size="sm" icon className="mb-12px">{error}</Alert>
                )}

                {/* Verification confirmed badge */}
                <div className="flex items-center gap-8px mb-20px px-12px py-10px bg-green-50 border border-green-200 rounded-lg">
                    <svg className="w-20px h-20px text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.745 3.745 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
                    </svg>
                    <span className="text-14px font-sans font-medium text-green-700">
                        Checklist verified — all items completed and photos taken
                    </span>
                </div>

                {/* --- Hours worked --- */}
                {/* "How long did this take?", per person, on every work order.
                    On an hour-tracked job the fieldset names anyone still
                    missing and the Submit button waits for them. */}
                {crew.length > 0 && (
                    <div className="mb-20px">
                        <HoursWorkedFieldset
                            people={crew}
                            hours={hours}
                            onChange={setHours}
                            required={hoursRequired}
                            estimatedHours={estimatedHours}
                            quickAdd={quickAdd}
                        />
                    </div>
                )}

                {/* --- Signature capture --- */}
                <div className="mb-20px">
                    <SignatureCapture
                        onSave={setSignatureDataUrl}
                        label="Your Signature"
                    />
                </div>

                {/* --- Job notes --- */}
                {/* Same field as the Job Details panel's, pre-filled with
                    whatever was written there during the job. */}
                <div className="mb-16px">
                    <label
                        htmlFor="signoff-notes"
                        className="block text-14px font-sans font-semibold text-brand-text-muted mb-6px"
                    >
                        Job Notes (optional)
                    </label>
                    <textarea
                        id="signoff-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any additional comments about the completed work..."
                        rows={3}
                        className="w-full px-12px py-10px text-16px font-sans text-brand-text bg-white border border-gray-300 rounded-lg focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 focus:outline-none resize-none placeholder:text-brand-text-subtle"
                    />
                </div>
            </div>

            {/* --- Bottom action bar --- */}
            <div className="workflow-sign-off__footer px-16px pb-16px pt-8px border-t border-gray-100 flex flex-col gap-8px">
                <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    disabled={isSubmitting || !signatureDataUrl || missingHours.length > 0}
                >
                    Submit Work Order
                </Button>
                <Button
                    variant="ghost"
                    size="md"
                    fullWidth
                    onClick={onBack}
                    disabled={isSubmitting}
                >
                    Back to Verification
                </Button>
            </div>
        </div>
    );
};

export default WorkflowSignOff;
